class SupportHeroPredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('#support-hero-predictive-search');
    this.form = this.querySelector('form');
    this.searchTags = document.querySelectorAll('.support-hero__tag');
    this.currentQuery = '';
    this.searchTimeout = null;
    this.selectedIndex = -1;
    this.results = [];
    
    if (!this.input || !this.predictiveSearchResults) return;
    
    this.setupEventListeners();
  }

  setupEventListeners() {
    // Input events
    this.input.addEventListener('input', this.debounce(() => {
      this.onChange();
    }, 300).bind(this));

    // Keyboard navigation
    this.input.addEventListener('keydown', this.onKeyDown.bind(this));

    // Close dropdown when clicking outside
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) {
        this.close();
      }
    });

    // Prevent form submission on enter when results are open
    this.form.addEventListener('submit', (event) => {
      if (this.selectedIndex >= 0 && this.results.length > 0) {
        event.preventDefault();
        const selectedLink = this.results[this.selectedIndex];
        if (selectedLink) {
          window.location.href = selectedLink.href;
        }
      }
    });

    // Setup tag click handlers
    this.searchTags.forEach(tag => {
      tag.addEventListener('click', () => {
        const searchTerm = tag.dataset.searchTerm;
        this.input.value = searchTerm;
        this.onChange();
        this.input.focus();
      });
    });

    // Handle search button click in results
    this.predictiveSearchResults.addEventListener('click', (event) => {
      const searchButton = event.target.closest('[data-search-all]');
      if (searchButton) {
        this.form.submit();
      }
    });
  }

  onChange() {
    const searchTerm = this.input.value.trim();
    
    if (!searchTerm.length) {
      this.close();
      this.currentQuery = '';
      return;
    }

    if (searchTerm === this.currentQuery) {
      return;
    }

    this.currentQuery = searchTerm;
    this.getSearchResults(searchTerm);
  }

  getSearchResults(searchTerm) {
    // Clear any existing timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    // Show loading state
    this.predictiveSearchResults.innerHTML = '<div class="support-hero-predictive-search__loading">Searching...</div>';
    this.open();

    fetch(`${window.Shopify.routes.root}search/suggest?q=${encodeURIComponent(searchTerm)}&resources[type]=article&resources[limit]=5&section_id=support-hero-predictive-search`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.status);
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-support-hero-predictive-search');
        
        if (resultsMarkup) {
          this.predictiveSearchResults.innerHTML = resultsMarkup.innerHTML;
          this.updateResults();
          this.open();
        } else {
          this.close();
        }
      })
      .catch((error) => {
        console.error('Search error:', error);
        this.predictiveSearchResults.innerHTML = '<div class="support-hero-predictive-search__error">An error occurred. Please try again.</div>';
        this.searchTimeout = setTimeout(() => this.close(), 3000);
      });
  }

  updateResults() {
    this.results = Array.from(this.predictiveSearchResults.querySelectorAll('.support-hero-predictive-search__link'));
    this.selectedIndex = -1;
  }

  onKeyDown(event) {
    if (!this.isOpen()) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.selectNext();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.selectPrevious();
        break;
      case 'Escape':
        event.preventDefault();
        this.close();
        this.input.blur();
        break;
      case 'Enter':
        if (this.selectedIndex >= 0) {
          event.preventDefault();
          const selectedLink = this.results[this.selectedIndex];
          if (selectedLink) {
            window.location.href = selectedLink.href;
          }
        }
        break;
    }
  }

  selectNext() {
    if (this.selectedIndex < this.results.length - 1) {
      this.selectedIndex++;
      this.updateSelection();
    }
  }

  selectPrevious() {
    if (this.selectedIndex > -1) {
      this.selectedIndex--;
      this.updateSelection();
    }
  }

  updateSelection() {
    this.results.forEach((link, index) => {
      if (index === this.selectedIndex) {
        link.classList.add('selected');
        link.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      } else {
        link.classList.remove('selected');
      }
    });
  }

  open() {
    const resultsContainer = this.predictiveSearchResults.querySelector('.support-hero-predictive-search__results');
    if (resultsContainer) {
      resultsContainer.classList.add('active');
      this.input.setAttribute('aria-expanded', 'true');
    }
  }

  close() {
    const resultsContainer = this.predictiveSearchResults.querySelector('.support-hero-predictive-search__results');
    if (resultsContainer) {
      resultsContainer.classList.remove('active');
    }
    this.input.setAttribute('aria-expanded', 'false');
    this.selectedIndex = -1;
    this.results = [];
  }

  isOpen() {
    const resultsContainer = this.predictiveSearchResults.querySelector('.support-hero-predictive-search__results');
    return resultsContainer && resultsContainer.classList.contains('active');
  }

  debounce(fn, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

// Add loading and error styles
const style = document.createElement('style');
style.textContent = `
  .support-hero-predictive-search__loading,
  .support-hero-predictive-search__error {
    padding: 20px;
    text-align: center;
    font-size: 14px;
    color: #666;
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 0 0 8px 8px;
    margin-top: -1px;
  }

  .support-hero-predictive-search__error {
    color: #d32f2f;
  }

  @media screen and (min-width: 750px) {
    .support-hero-predictive-search__loading,
    .support-hero-predictive-search__error {
      border-radius: 0 0 10px 10px;
    }
  }
`;
document.head.appendChild(style);

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    customElements.define('support-hero-predictive-search', SupportHeroPredictiveSearch);
  });
} else {
  customElements.define('support-hero-predictive-search', SupportHeroPredictiveSearch);
}